/* global console, document, HTMLAnchorElement, Office */

import { traceActivityHandler } from '../activity-handler/trace-activity-handler';
import { writeTabularSheetToWorksheet } from '../shared/sheet-writer/write-tabular-sheet-to-worksheet.util';
import { saveTraceSheetFiltersNamedItemForSheet } from './utils/trace-sheet-filter-named-item.util';
import { traceTokenStore } from '../token/trace-token-store';
import { traceUserInfoStore } from '../user-info/trace-user-info-store';
import { TaskPaneOfficeAuthFlow } from './auth-flow';
import { syncTaskPaneChromeWithAuthState } from './chrome-sync';
import { ReportingTaskPaneFilters } from './reporting-filters';
import { taskPaneUiSession } from './session';
import {
    CONNECT_BUTTON_LABEL_SIGN_IN,
    CONTINUE_SWITCH_ORGANISATION_BUTTON_LABEL,
    SWITCH_ORGANISATION_BUTTON_LABEL,
    TRACE_ADDIN_PRIVACY_POLICY_URL,
    TRACE_ADDIN_TERMS_OF_USE_URL,
} from './taskpane.constants';
import { logAuthDebug } from './utils/auth-debug.util';
import {
    clearOrgCodeFieldValidation,
    getPersistedOrgCodeForTitle,
    restoreLastOrgCodeInputFromSession,
    setOrgCodeInputDisabled,
} from './utils/org-code-field.util';
import { confirmFetchIfActiveSheetMayBeOverwritten } from './utils/overwrite-dialog.util';
import { clearReportFilterFieldHighlights } from './utils/report-filter-field-validation-highlights.util';
import { setupTaskpaneActionHandlers } from './utils/setup-taskpane-action-handlers.util';
import { registerTraceSheetFilterWorksheetActivation } from './utils/register-trace-sheet-filter-worksheet-activation.util';

/**
 * Tokens are session-scoped: {@link traceTokenStore} uses `sessionStorage`, so they survive task-pane
 * reloads until the browsing context ends (exact desktop WebView lifecycle varies by host). Not
 * cross-device. Signing out clears them.
 */

const reportDataCard = new ReportingTaskPaneFilters({
    onSyncUi: () => syncTaskPaneChromeFromAuth(),
    tokens: traceTokenStore,
    userInfo: traceUserInfoStore,
    agreementsApi: traceActivityHandler,
    materialSummaryApi: traceActivityHandler,
    tabularSheetWriter: { writeTabularSheetToWorksheet },
    fetchConfirm: { confirmFetchIfActiveSheetMayBeOverwritten },
    sheetFilters: {
        saveAfterSuccessfulFetch: async ({ sheetId, snapshot }) => {
            try {
                await saveTraceSheetFiltersNamedItemForSheet(sheetId, snapshot);
            } catch (e) {
                console.warn('[trace-reports-excel-addin] Could not persist sheet filters', e);
            }
        },
    },
});

/** Set when worksheet filter activation is registered; re-run after login so restore sees a session. */
let scheduleTraceSheetFilterRestore: () => void = () => {};

const officeAuthFlow = new TaskPaneOfficeAuthFlow({
    syncUi: syncTaskPaneChromeFromAuth,
    afterSuccessfulLogin: () => {
        taskPaneUiSession.setSwitchOrganisationFlowActive(false);
        syncTaskPaneChromeFromAuth();
        void (async () => {
            await reportDataCard.refreshUserInfoAndRenderPanels();
            scheduleTraceSheetFilterRestore();
        })();
    },
});

// ---------------------------------------------------------------------------
// Excel task pane entry
// ---------------------------------------------------------------------------

Office.onReady((info) => {
    if (info.host !== Office.HostType.Excel) {
        return;
    }

    bootstrapTraceReportsTaskPane();
});

/** One-time startup when the add-in task pane loads in Excel. */
function bootstrapTraceReportsTaskPane(): void {
    wireLegalLinkHrefs();
    wireAddinVersionLabel();
    restoreLastOrgCodeInputFromSession();
    registerTaskPaneDomHandlers();
    syncTaskPaneChromeFromAuth();
    reportDataCard.handleReportingTargetChange();
    void (async () => {
        if (traceTokenStore.hasConnectedSession()) {
            await reportDataCard.refreshUserInfoAndRenderPanels();
        }
        scheduleTraceSheetFilterRestore = registerTraceSheetFilterWorksheetActivation({
            tokens: traceTokenStore,
            userInfo: traceUserInfoStore,
            reporting: reportDataCard,
        });
    })();
}

function wireAddinVersionLabel(): void {
    const el = document.getElementById('trace-addin-version');
    if (el) {
        el.textContent = `Version ${__TRACE_ADDIN_MANIFEST_VERSION__}`;
    }
}

function wireLegalLinkHrefs(): void {
    const terms = document.getElementById('legal-link-terms') as HTMLAnchorElement | null;
    const privacy = document.getElementById('legal-link-privacy') as HTMLAnchorElement | null;
    if (terms) {
        terms.href = TRACE_ADDIN_TERMS_OF_USE_URL;
    }
    if (privacy) {
        privacy.href = TRACE_ADDIN_PRIVACY_POLICY_URL;
    }
}

function registerTaskPaneDomHandlers(): void {
    setupTaskpaneActionHandlers({
        onOrgCodeInput: clearOrgCodeFieldValidation,
        onConnectClick: () => officeAuthFlow.handleConnectButtonClick(),
        onSwitchOrganisationCancelClick: () => officeAuthFlow.handleSwitchOrganisationCancel(),
        onFetchReport: () => {
            void reportDataCard.fetchReport({ intent: 'replace' });
        },
        onRefreshReportData: () => {
            void reportDataCard.fetchReport({ intent: 'refresh' });
        },
        onReportTargetChange: () => reportDataCard.handleReportingTargetChange(),
        onFilterRecordTypeChange: () => {
            clearReportFilterFieldHighlights();
            reportDataCard.resetReportingUi({ clearSummary: true });
            reportDataCard.syncRefreshButtonState();
        },
        onFiltersDomActivity: () => reportDataCard.syncRefreshButtonState(),
        onReportFiltersPanelDomActivity: () => {
            clearReportFilterFieldHighlights();
            reportDataCard.syncRefreshButtonState();
        },
    });
}

function syncTaskPaneChromeFromAuth(): void {
    syncTaskPaneChromeWithAuthState({
        tokenStore: traceTokenStore,
        session: taskPaneUiSession,
        userInfoStore: traceUserInfoStore,
        getPersistedOrgCodeForTitle,
        setOrgCodeInputDisabled,
        clearOrgCodeFieldValidation,
        logAuthDebug,
        reporting: reportDataCard,
        officeAuth: officeAuthFlow,
        labels: {
            switchOrganisation: SWITCH_ORGANISATION_BUTTON_LABEL,
            continueSwitchOrganisation: CONTINUE_SWITCH_ORGANISATION_BUTTON_LABEL,
            connectSignIn: CONNECT_BUTTON_LABEL_SIGN_IN,
        },
    });
}
